import React, { useState, useMemo } from "react";
import api from "@/lib/api";
import { useLiveQuery, TAGS } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info, Fire, Car, TrendUp } from "@phosphor-icons/react";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAY_RANGES = [
  { value: "7",  label: "Son 7 gün" },
  { value: "14", label: "Son 14 gün" },
  { value: "28", label: "Son 28 gün" },
  { value: "60", label: "Son 60 gün" },
  { value: "90", label: "Son 90 gün" },
];
const LOT_OPTS = [
  { value: "all",     label: "Tüm Otoparklar" },
  { value: "ust_kat", label: "Üst Kat" },
  { value: "alt_kat", label: "Alt Kat" },
];
const METRIC_OPTS = [
  { value: "avg_occupancy", label: "Ortalama Doluluk (araç)", desc: "Aynı saatte park halinde olan ortalama araç sayısı" },
  { value: "entry_count",   label: "Giriş Sayısı",             desc: "O saat aralığında otoparka giriş yapan araç sayısı" },
];

// HSL-based color scale: 0 → gray/near-white, 1 → deep primary
function colorForRatio(r) {
  if (r <= 0) return "hsl(0, 0%, 96%)";
  // 0 → light blue-gray, 1 → deep red/orange (fire)
  // Use HSL: hue from 210 (cool) → 12 (warm), saturation from 25 → 85, lightness from 92 → 42
  const clamped = Math.max(0, Math.min(1, r));
  const hue = 210 - clamped * 198;      // 210 → 12
  const sat = 25 + clamped * 60;         // 25 → 85
  const light = 92 - clamped * 50;       // 92 → 42
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

function textColorForRatio(r) {
  return r > 0.55 ? "#fff" : "#111";
}

export default function OccupancyHeatmap() {
  const [days, setDays] = useState("28");
  const [lot, setLot] = useState("all");
  const [metric, setMetric] = useState("avg_occupancy");

  const { data, loading, error } = useLiveQuery(
    [TAGS.SESSIONS],
    async () => {
      const params = new URLSearchParams();
      params.set("days", days);
      if (lot !== "all") params.set("lot", lot);
      const r = await api.get(`/reports/occupancy-heatmap?${params.toString()}`);
      return r.data;
    },
    { deps: [days, lot], intervalMs: 60000 },
  );

  const cells = useMemo(() => data?.cells || [], [data]);
  const dowLabels = data?.dow_labels || ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
  const maxVal = useMemo(() => {
    if (!cells.length) return 0;
    return cells.reduce((m, c) => Math.max(m, c[metric] || 0), 0);
  }, [cells, metric]);

  // Grid[dow][hour] lookup
  const grid = useMemo(() => {
    const g = Array.from({ length: 7 }, () => Array(24).fill(null));
    for (const c of cells) g[c.dow][c.hour] = c;
    return g;
  }, [cells]);

  // Peak hour analysis
  const peak = useMemo(() => {
    if (!cells.length) return null;
    let best = null;
    for (const c of cells) {
      const v = c[metric] || 0;
      if (v > 0 && (!best || v > best[metric])) best = c;
    }
    return best;
  }, [cells, metric]);

  // Weekend vs weekday averages
  const analytics = useMemo(() => {
    if (!cells.length) return null;
    let weekdaySum = 0, weekdayCount = 0, weekendSum = 0, weekendCount = 0;
    for (const c of cells) {
      const v = c[metric] || 0;
      if (c.dow < 5) { weekdaySum += v; weekdayCount++; }
      else { weekendSum += v; weekendCount++; }
    }
    return {
      weekday: weekdayCount ? (weekdaySum / weekdayCount) : 0,
      weekend: weekendCount ? (weekendSum / weekendCount) : 0,
    };
  }, [cells, metric]);

  const totalEntries = useMemo(
    () => cells.reduce((s, c) => s + (c.entry_count || 0), 0),
    [cells],
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Filters */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Zaman Aralığı</div>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="rounded-sm h-10" data-testid="heatmap-days-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAY_RANGES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Otopark</div>
              <Select value={lot} onValueChange={setLot}>
                <SelectTrigger className="rounded-sm h-10" data-testid="heatmap-lot-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOT_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Metrik</div>
              <Select value={metric} onValueChange={setMetric}>
                <SelectTrigger className="rounded-sm h-10" data-testid="heatmap-metric-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METRIC_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 text-xs text-muted-foreground flex items-start gap-2">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>{METRIC_OPTS.find(m => m.value === metric)?.desc}. Değerler seçili aralıktaki tüm hafta içi/hafta sonu günlerin ortalamasıdır.</span>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-sm border-red-400/40 bg-red-50">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-red-800 flex items-center gap-1 font-semibold">
              <Fire size={13} /> En Yoğun Saat
            </div>
            <div className="text-2xl md:text-3xl font-bold mt-2 text-red-700 font-mono" data-testid="heatmap-peak">
              {loading ? "..." : peak ? `${peak.dow_label} ${String(peak.hour).padStart(2, "0")}:00` : "—"}
            </div>
            <div className="text-[10px] text-red-800/70 mt-1">
              {peak ? (metric === "avg_occupancy"
                ? `Ort. ${peak.avg_occupancy} araç`
                : `${peak.entry_count} giriş`) : "Bu aralıkta veri yok"}
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-blue-400/40 bg-blue-50">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-blue-800 flex items-center gap-1 font-semibold">
              <Car size={13} /> Toplam Giriş
            </div>
            <div className="text-2xl md:text-3xl font-bold mt-2 text-blue-700 font-mono" data-testid="heatmap-total-entries">
              {loading ? "..." : totalEntries.toLocaleString("tr-TR")}
            </div>
            <div className="text-[10px] text-blue-800/70 mt-1">
              {days} günlük araç trafiği
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-amber-400/40 bg-amber-50">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-amber-800 flex items-center gap-1 font-semibold">
              <TrendUp size={13} /> Hafta İçi / Hafta Sonu
            </div>
            <div className="text-2xl md:text-3xl font-bold mt-2 text-amber-700 font-mono" data-testid="heatmap-weekday-weekend">
              {loading || !analytics
                ? "..."
                : `${analytics.weekday.toFixed(1)} / ${analytics.weekend.toFixed(1)}`}
            </div>
            <div className="text-[10px] text-amber-800/70 mt-1">
              Ortalama {metric === "avg_occupancy" ? "doluluk" : "giriş"} (araç)
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Heatmap grid */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold uppercase tracking-widest text-sm">
              Doluluk Isı Haritası · Saat × Gün
            </h3>
            <div className="text-[10px] text-muted-foreground">
              {loading ? "Yükleniyor..." : data?.range?.start
                ? `${new Date(data.range.start).toLocaleDateString("tr-TR")} → ${new Date(data.range.end).toLocaleDateString("tr-TR")}`
                : ""}
            </div>
          </div>

          {error && (
            <div className="text-sm text-destructive p-4 rounded-sm bg-destructive/5 border border-destructive/20">
              Isı haritası yüklenemedi.
            </div>
          )}

          {!error && (
            <div className="overflow-x-auto" data-testid="heatmap-grid">
              <table className="w-full text-[10px] md:text-xs" style={{ borderCollapse: "separate", borderSpacing: "2px" }}>
                <thead>
                  <tr>
                    <th className="text-left font-normal text-muted-foreground pr-2 sticky left-0 bg-card" style={{ minWidth: 32 }}></th>
                    {HOURS.map(h => (
                      <th key={h} className="font-normal text-muted-foreground text-center" style={{ minWidth: 28 }}>
                        <span className={h % 2 === 0 ? "" : "opacity-40"}>{String(h).padStart(2, "0")}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dowLabels.map((lbl, dow) => (
                    <tr key={dow}>
                      <td className="font-semibold pr-2 text-right sticky left-0 bg-card" data-testid={`heatmap-row-${dow}`}>
                        <span className={dow >= 5 ? "text-amber-700" : ""}>{lbl}</span>
                      </td>
                      {HOURS.map(h => {
                        const cell = grid[dow][h];
                        const v = cell ? (cell[metric] || 0) : 0;
                        const ratio = maxVal > 0 ? v / maxVal : 0;
                        const bg = colorForRatio(ratio);
                        const fg = textColorForRatio(ratio);
                        const title = `${lbl} ${String(h).padStart(2, "0")}:00\nOrt. Doluluk: ${cell?.avg_occupancy ?? 0} araç\nGiriş: ${cell?.entry_count ?? 0}\nÖrneklem: ${cell?.sample_count ?? 0} gün`;
                        return (
                          <td
                            key={h}
                            title={title}
                            data-testid={`heatmap-cell-${dow}-${h}`}
                            className="text-center rounded-sm transition-all hover:scale-110 cursor-default"
                            style={{
                              backgroundColor: bg,
                              color: fg,
                              minWidth: 28,
                              height: 32,
                              fontWeight: v > 0 ? 600 : 400,
                            }}
                          >
                            {loading ? "" : v > 0 ? (metric === "avg_occupancy" ? v.toFixed(v < 10 ? 1 : 0) : v) : ""}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="mt-4 flex items-center gap-3 text-[11px] text-muted-foreground">
            <span>Düşük</span>
            <div className="flex-1 max-w-md h-3 rounded-sm" style={{
              background: `linear-gradient(90deg, ${colorForRatio(0)}, ${colorForRatio(0.25)}, ${colorForRatio(0.5)}, ${colorForRatio(0.75)}, ${colorForRatio(1)})`,
            }} />
            <span>Yüksek</span>
            <span className="ml-2 font-mono">Maks: {maxVal.toFixed(metric === "avg_occupancy" ? 1 : 0)}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
